import { injectable, inject } from 'inversify';

import { type IRpcDispatcher } from '@evercall/json-rpc-dispatcher';
import DITypes from '@evercall/json-rpc-dispatcher/DITypes';
import Request from '@framework/api/Rpc/Request';

import { IOaccConference } from '../../domain/vo/Conference';
import {
  IOaccRegistrationDetails,
  IOaccRegistrationDetailsViewer,
} from '../../domain/vo/RegistrationDetails';
import { IDialIn } from '@evercall/domain/vo/DialIn';
import { ICustomRegistration } from '@evercall/domain/vo/call/CustomRegistration';
import { IOaccRegistrationGroup } from '../../domain/vo/RegistrationGroup';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';

@injectable()
class OaccRepository {
  constructor(
    @inject(DITypes.RpcDispatcher) private rpc: IRpcDispatcher,
    @inject(DITypes.WebServiceRpcDispatcher) private webServiceRpc: IRpcDispatcher,
  ) {}

  findConference = async (payload: { id: string }): Promise<IOaccConference> => {
    const response = await this.rpc.call<RpcSuccess>(new Request('evercall.oacc.find_conference', payload));
    return response.getResult().payload;
  };

  /**
   * This method returns null when isApproveDenyRegistrantsEnabled flag is set to true.
   * This is done at DM Portal -> Evercall Profile -> Restrict Access panel.
   */
  registerParticipantToConference = async (
    { conferenceId, ssid }: { conferenceId: string, ssid: string },
  ): Promise<IOaccRegistrationDetails | null> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request(
        'evercall.oacc.register_conference_participant_evercall', { conferenceId }, { ssid }),
    );
    return response.getResult().payload;
  };

  getDialInPhones = async (): Promise<IDialIn[]> => {
    const response = await this.webServiceRpc.call<RpcSuccess>(new Request('evercall.guest.get_intl_dial_ins', {}));
    return response.getResult().payload.collection;
  };

  getExportConferenceToCalendarUrl = async (payload: IOaccRegistrationDetails): Promise<string> => {
    const response = await this.rpc.call<RpcSuccess>(new Request('evercall.oacc.generate_call_details_icalendar_link', payload));
    return response.getResult().payload;
  };

  getExportConferenceToCalendarUrlRoadshow = async (payload: IOaccRegistrationDetails): Promise<string> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('evercall.viewer.oacc.generate_participant_conference_details_calendar', payload),
    );
    return response.getResult().payload;
  };

  sendDetailsViaEmailRoadshow = async (payload: IOaccRegistrationDetails): Promise<null> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('evercall.viewer.oacc.send_participant_conference_details_email', payload),
    );
    return response.getResult().payload;
  };

  /**
   * Get Evercall conference details
   *
   * null is returned if:
   * 1) "Embed Dial-Ins into Roadshow" in evercall Condor profile is unchecked
   * 2) "Embed Dial-Ins into Roadshow" in evercall Condor profile is checked but Conference ID is no set
   * 3) User is blocked
   *
   * response.isConferenceValid will be false if the provided conference does not exist
   */
  getEvercallDetailsRoadshow = async (payload: { roadshowId: string }): Promise<IOaccRegistrationDetailsViewer | null> => {
    let req = new Request('evercall.viewer.oacc.get_participant_conference_details', payload);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  };

  registerParticipantToConferenceOnViewer = async (
    payload: { roadshowId: string },
  ): Promise<{ participantId: string, participantCorporateEmail: string } | null> => {
    const req = new Request('evercall.viewer.oacc.register_participant', payload);
    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  };

  getCallCustomRegistrationSettingsByConferenceId = async (
    payload: { conferenceId: string },
  ): Promise<ICustomRegistration> => {
    const req = new Request('evercall.oacc.get_call_appearance_settings', payload);
    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  };

  getRegistrationGroup = async (
    { path, ssid }: { path: string, ssid: string | null },
  ): Promise<IOaccRegistrationGroup> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('evercall.oacc.get_group', { path }, ...(ssid ? [{ ssid }] : [])),
    );
    return response.getResult().payload;
  };
}

export default OaccRepository;
